import type { JSX } from '@builder.io/qwik';
import { component$, useContext, $ } from '@builder.io/qwik';
import MobileNavItem from '~/components/Layout/Header/Mobile/MobileNavItem';
import { cva } from 'class-variance-authority';
import {
  getCSSColorText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';
import DropdownIcon from '~/icons/dropdown';
import type { LinkValueInterface } from '~/types/AppData/Layout/LinkValueInterface';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import type { NavItemType } from '~/types/NavItemType';
import { ConditionalWrapper } from '~/components/Shared/Utility/ConditionalWrapper/ConditionalWrapper';

const textClasses = cva('mobile-nav-top', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
  },
});

const subnavClasses = cva('mobile-nav-sub my-5 mx-2.5 flex flex-wrap', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
  },
});

const openSubnav = (
  item: NavItemType['item'],
  activeItem: NavItemType['activeItem'],
  isIcon: boolean
) => {
  // Some properties, like Mental Floss, have item.link.url set to '-' when there's no link
  const itemUrl = item.link.url !== '-' ? item.link.url : '';

  if ((isIcon && itemUrl) || (!isIcon && !itemUrl)) {
    activeItem.value =
      item.link.text === activeItem.value ? '' : item.link.text;
  }
};

export default component$(
  ({ item, activeItem, level }: NavItemType): JSX.Element => {
    const appData = useContext(AppDataContext);
    const active = item.link.text === activeItem.value;

    return (
      <li class='list-none mb-5 leading-5 uppercase'>
        <span
          class='w-full flex justify-between cursor-pointer'
          onClick$={() => openSubnav(item, activeItem, false)}
        >
          <h4
            class={textClasses({
              color: active
                ? appData.themeComponents.header.hamburgerMenuLinksActiveColor
                    .value
                : appData.themeComponents.header.hamburgerMenuLinksNormalColor
                    .value,
              ...getFontSizeObject(
                appData.themeComponents.header,
                'hamburgerMenuLinksFontSize'
              ),
            })}
          >
            {/* Sometimes the item is only for opening submenu items, sometimes it's a link itself */}
            <ConditionalWrapper
              component='a'
              condition={!!item.link.url && item.link.url !== '-'}
              props={{ href: item.link.url }}
            >
              {item.link.text}
            </ConditionalWrapper>
          </h4>
          <DropdownIcon
            onClick$={$(() => openSubnav(item, activeItem, true))}
            active={active}
            color={
              appData.themeComponents.header.hamburgerMenuLinksNormalColor.value
            }
            activeColor={
              appData.themeComponents.header.hamburgerMenuLinksActiveColor.value
            }
          />
        </span>
        {active && (
          <span>
            <ul
              class={subnavClasses({
                color:
                  appData.themeComponents.header
                    .hamburgerMenuSubLinksNormalColor.value,
                ...getFontSizeObject(
                  appData.themeComponents.header,
                  'hamburgerMenuSubLinksFontSize'
                ),
              })}
            >
              {item.children?.map((each: LinkValueInterface, index) => {
                return (
                  <MobileNavItem
                    key={index}
                    item={{
                      ...each,
                      width:
                        (item.children?.length ?? 0) > 10 ? 'half' : 'full',
                    }}
                    activeItem={activeItem}
                    level={level + 1}
                  />
                );
              })}
            </ul>
          </span>
        )}
      </li>
    );
  }
);
